import { Component, DestroyRef, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Filter } from './filters';
import { FilterComponent } from './filter/filter.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FiltersFormBuilderService } from './filters-form-builder.service';
import { MatIconModule } from '@angular/material/icon';
import { auditTime, map, Subject, takeUntil, tap } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'cbre-filters',
  imports: [CommonModule, MatIconModule, FilterComponent, ReactiveFormsModule],
  providers: [FiltersFormBuilderService],
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss',
})
export class FiltersComponent {
  readonly filters = input<Filter[]>();
  readonly aggregations = input<Record<string, any>>();
  readonly canRemoveFilter = input(false);

  readonly removeFilter = output<Filter>();
  readonly valueChanges = output<Record<string, any>>();
  readonly filtersChange = output<Record<string, any>>();

  /** Backward-compatible alias for templates using filters$() */
  readonly filters$ = this.filters;

  public form: FormGroup<Record<string, any>> = new FormGroup({});

  private readonly _reset$ = new Subject<void>();
  private readonly _formBuilder = inject(FiltersFormBuilderService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const filters = this.filters();
      if (filters) {
        this._reset$.next();
        this.form = this._formBuilder.buildForm(filters);
        this._setupFormListeners();
      }
    });
  }

  private _setupFormListeners(): void {
    this.form.valueChanges
      .pipe(
        takeUntil(this._reset$),
        takeUntilDestroyed(this.destroyRef),
        auditTime(250),
        tap(value => this.valueChanges.emit(value)),
        map(value => this.mapFilterValues(this.filters$(), value))
      )
      .subscribe(filters => this.filtersChange.emit(filters));
  }

  private readonly mapFilterValues = (filters: Filter[], formValues: Record<string, any>): Filter[] => {
    return filters.map(filter => {
      const values =
        filter.type === 'group' ? this.mapFilterValues(filter.associations, formValues) : formValues[filter.key];
      return { ...filter, values: values };
    });
  };

}
